/**
 * F1 Fantasy Constants
 *
 * Single source of truth for all F1 Fantasy game constants.
 * All other files should import from here to avoid inconsistencies.
 */

// ============================================================================
// Season Constants
// ============================================================================

/**
 * The current active F1 season. Update this each year.
 * All pages that need a season fallback should import this instead of hardcoding.
 */
export const DEFAULT_SEASON = 2026;

/**
 * Seasons with fantasy data available, newest first.
 * Used by season selector dropdowns across pages.
 */
export const FANTASY_SEASONS: number[] = [2026, 2025, 2024, 2023];

// ============================================================================
// Team Composition & Budget Constants
// ============================================================================

/** Number of drivers in a fantasy team */
export const DRIVERS_PER_TEAM = 5;

/** Number of constructors in a fantasy team */
export const CONSTRUCTORS_PER_TEAM = 2;

/** Starting budget cap in millions — every team starts the season with $100M */
export const BUDGET_CAP = 100;

/**
 * Fallback budget slider range used when a meaningful range cannot be
 * computed from price history (no data yet, or degenerate result).
 */
export const BUDGET_RANGE_FALLBACK = { min: 80, max: 150 } as const;

/**
 * Minimum acceptable width for the computed budget slider range.
 * If the computed range is narrower (e.g. all price gains are 0 because
 * no season history exists), fall back to BUDGET_RANGE_FALLBACK instead
 * of presenting a useless near-zero range like 99–101.
 */
export const MIN_BUDGET_RANGE_WIDTH = 10;

// ============================================================================
// Price Tier Constants
// ============================================================================

/**
 * Official tier threshold per season — like the price floor, this is a
 * per-season game rule, not a universal constant.
 * - Tier A: >= threshold (smaller price swings: ±0.1/±0.3)
 * - Tier B: < threshold (larger price swings: ±0.2/±0.6)
 *
 * 2023–2025: $19.0M. 2026: $18.5M (matches f1fantasytools.com).
 */
export const SEASON_TIER_THRESHOLDS: Record<number, number> = {
  2023: 19.0,
  2024: 19.0,
  2025: 19.0,
  2026: 18.5,
};

/**
 * Resolve the Tier A/B price threshold for a season.
 * Unknown seasons fall back to the most recent known season's threshold
 * (rules carry forward until a new season's rules are confirmed).
 */
export function getTierThreshold(season: number): number {
  const known = SEASON_TIER_THRESHOLDS[season];
  if (known !== undefined) return known;
  const latestSeason = Math.max(
    ...Object.keys(SEASON_TIER_THRESHOLDS).map(Number),
  );
  return SEASON_TIER_THRESHOLDS[latestSeason];
}

/**
 * Known price floors per season — drivers/constructors cannot go below this price.
 * The floor is a property of each season's game rules, NOT a universal constant
 * (2025 floor was $4.5M; 2026 already has drivers at $3.2M).
 *
 * Only add a season here once its floor is confirmed from official game rules.
 * For seasons not listed, getPriceFloor() derives a working floor from the
 * lowest observed price in the data.
 */
export const SEASON_PRICE_FLOORS: Record<number, number> = {
  2023: 4.5,
  2024: 4.5,
  2025: 4.5,
};

/**
 * Resolve the effective price floor for a season.
 *
 * - Known season → confirmed floor from SEASON_PRICE_FLOORS.
 * - Unknown season + observed prices → lowest observed price (the cheapest
 *   asset is at or above the real floor, so this is the best safe estimate;
 *   it self-corrects as prices drop).
 * - Unknown season, no data → 0 (disables floor clipping rather than
 *   clipping against a wrong floor).
 *
 * @param season - Season year (e.g. 2026)
 * @param observedPrices - Current prices of all drivers/constructors in the pool
 */
export function getPriceFloor(
  season: number,
  observedPrices?: readonly number[],
): number {
  const known = SEASON_PRICE_FLOORS[season];
  if (known !== undefined) return known;
  if (observedPrices && observedPrices.length > 0) {
    const min = Math.min(...observedPrices.filter((p) => p > 0));
    if (Number.isFinite(min)) return Math.round(min * 10) / 10;
  }
  return 0;
}

/**
 * The per-season pricing rules needed by price-change calculations.
 * Bundle resolved once per asset pool via getSeasonPricing and passed down,
 * so the rules live in exactly one place.
 */
export interface SeasonPricing {
  /** Price floor — assets cannot drop below this (see getPriceFloor) */
  priceFloor: number;
  /** Tier A threshold — assets at/above get smaller price swings */
  tierThreshold: number;
}

/**
 * Resolve all per-season pricing rules for an asset pool.
 *
 * @param season - Season year
 * @param observedPrices - Current prices of the pool (drivers OR constructors),
 *   used to derive the floor for seasons without a confirmed value
 */
export function getSeasonPricing(
  season: number,
  observedPrices?: readonly number[],
): SeasonPricing {
  return {
    priceFloor: getPriceFloor(season, observedPrices),
    tierThreshold: getTierThreshold(season),
  };
}

// ============================================================================
// Price Change Buckets
// ============================================================================

/** Tier A price change columns (smaller swings for premium assets) */
export const TIER_A_COLUMNS = ["-0.3", "-0.1", "+0.1", "+0.3"] as const;

/**
 * Tier B price change columns (larger swings for budget assets)
 *
 * Standard levels: -0.6 (terrible), -0.2 (poor), +0.2 (good), +0.6 (great)
 * Edge cases (not shown by default):
 *   - "0.0" only appears when a driver is at the season's price floor and
 *     cannot drop further — all negative outcomes are clipped to 0.
 *   - A partial-drop column (e.g. "-0.4") appears when a driver is NEAR the
 *     floor and a standard negative change would exceed the max achievable drop.
 */
export const TIER_B_COLUMNS = ["-0.6", "-0.2", "+0.2", "+0.6"] as const;

export type TierAColumn = (typeof TIER_A_COLUMNS)[number];
export type TierBColumn = (typeof TIER_B_COLUMNS)[number];
export type PriceChangeColumn = TierAColumn | TierBColumn;

// ============================================================================
// PPM Thresholds
// ============================================================================

/**
 * PPM (Points Per Million) thresholds for price change determination
 * These define the performance bands that map to price changes
 */
export const PPM_THRESHOLDS = {
  GREAT: 1.2, // Great performance -> positive price change
  GOOD: 0.9, // Good performance -> small positive change
  POOR: 0.6, // Poor performance -> neutral or small negative
  // Below 0.6 = Terrible -> negative price change
} as const;

/**
 * Additional PPM threshold for Tier B separating "poor" from "terrible"
 * Below this → -0.6 (terrible); above → -0.2 (poor)
 */
export const TIER_B_POOR_THRESHOLD = 0.4 as const;

// ============================================================================
// Price Change Values
// ============================================================================

/** Tier A price changes (smaller swings for >= $19M) */
export const TIER_A_PRICE_CHANGES = {
  GREAT: 0.3,
  GOOD: 0.1,
  POOR: -0.1,
  TERRIBLE: -0.3,
} as const;

/** Tier B price changes (larger swings for < $19M) */
export const TIER_B_PRICE_CHANGES = {
  GREAT: 0.6,
  GOOD: 0.2,
  POOR: -0.2,
  TERRIBLE: -0.6,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determine price tier based on current price
 *
 * @param price - Driver/constructor price in millions
 * @param tierThreshold - The season's Tier A threshold (from getTierThreshold)
 * @returns "A" if >= threshold, "B" otherwise
 */
export function determineTier(price: number, tierThreshold: number): "A" | "B" {
  return price >= tierThreshold ? "A" : "B";
}

/**
 * Get price change columns for a given tier
 *
 * @param tier - "A" or "B"
 * @returns Array of price change bucket strings
 */
export function getPriceChangeColumns(tier: "A" | "B"): readonly string[] {
  return tier === "A" ? TIER_A_COLUMNS : TIER_B_COLUMNS;
}

/**
 * Check if a price is at or below the price floor
 *
 * @param price - Current price in millions
 * @param priceFloor - The season's price floor (from getPriceFloor)
 * @returns true if at or below floor
 */
export function isAtPriceFloor(price: number, priceFloor: number): boolean {
  return price <= priceFloor;
}

/**
 * Remap standard Tier B price change probabilities to account for the price floor.
 *
 * For drivers near the price floor, some standard negative changes would bring the
 * price below the floor. Those outcomes are clipped to the actual max drop, and
 * their probabilities are combined into the clipped column.
 *
 * Example: driver at $4.9M with floor $4.5M, max drop = $0.4M
 *   "-0.6" (4.9 − 0.6 = 4.3 < 4.5) → clips to "-0.4"; probability added to "-0.4"
 *   "-0.5" (4.9 − 0.5 = 4.4 < 4.5) → clips to "-0.4"; probability added to "-0.4"
 *   "-0.2" onwards: unchanged
 *   Result: { "-0.4": combinedProb, "-0.2": …, "0.0": …, "+0.2": …, "+0.6": … }
 *
 * @param currentPrice - Driver's current price in millions
 * @param probs - Standard probability distribution keyed by TIER_B_COLUMNS
 * @returns Effective probability distribution; keys may differ from TIER_B_COLUMNS
 */
/**
 * Remap standard Tier B price change probabilities to account for the price floor,
 * always returning exactly 4 display columns for consistent card layouts.
 *
 * Standard columns: ["-0.6", "-0.2", "+0.2", "+0.6"]
 *
 * Behaviour by driver price (relative to the season's floor F):
 *   ≥ F+0.6   → standard 4 columns unchanged
 *   F+0.3–0.5 → replace -0.6 with partial drop (e.g. "-0.5", "-0.4", "-0.3")
 *   F+0.2     → -0.6 clips to -0.2; keep -0.6 at 0 % for 4-column consistency
 *   F+0.1     → both negatives clip to "-0.1"; show ["-0.1", "-0.2"@0%, "+0.2", "+0.6"]
 *   F (floor) → both negatives clip to 0.0; show ["-0.2"@0%, "0.0", "+0.2", "+0.6"]
 *
 * @param priceFloor - The season's price floor (from getPriceFloor). A floor
 *   of 0 (unknown season, no data) disables clipping entirely.
 */
export function applyPriceFloorToTierBProbabilities(
  currentPrice: number,
  probs: Record<string, number>,
  priceFloor: number,
): Record<string, number> {
  // Fast path: all 4 standard columns are fully achievable
  if (currentPrice >= priceFloor + 0.6) {
    return probs;
  }

  const maxDrop = Math.round((currentPrice - priceFloor) * 10) / 10;

  if (maxDrop > 0.2) {
    // -0.6 clips to a unique value between -0.2 and -0.6 (e.g. -0.5, -0.4, -0.3)
    // Replace -0.6 with this custom column; -0.2 and positives stay unchanged
    const clippedCol = (-maxDrop).toFixed(1);
    return {
      [clippedCol]: probs["-0.6"] ?? 0,
      "-0.2": probs["-0.2"] ?? 0,
      "+0.2": probs["+0.2"] ?? 0,
      "+0.6": probs["+0.6"] ?? 0,
    };
  }

  if (maxDrop === 0.2) {
    // -0.6 clips to -0.2 (same standard column) → combine, keep -0.6 at 0% for 4-column UX
    return {
      "-0.6": 0,
      "-0.2": (probs["-0.6"] ?? 0) + (probs["-0.2"] ?? 0),
      "+0.2": probs["+0.2"] ?? 0,
      "+0.6": probs["+0.6"] ?? 0,
    };
  }

  if (maxDrop > 0) {
    // Both -0.6 and -0.2 exceed the floor; clip to partial drop (e.g. "-0.1")
    // Keep -0.2 at 0% as a 4th-column placeholder
    const clippedCol = (-maxDrop).toFixed(1);
    return {
      [clippedCol]: (probs["-0.6"] ?? 0) + (probs["-0.2"] ?? 0),
      "-0.2": 0,
      "+0.2": probs["+0.2"] ?? 0,
      "+0.6": probs["+0.6"] ?? 0,
    };
  }

  // maxDrop === 0: driver is at the price floor — all negatives clip to 0.0
  return {
    "-0.2": 0,
    "0.0": (probs["-0.6"] ?? 0) + (probs["-0.2"] ?? 0),
    "+0.2": probs["+0.2"] ?? 0,
    "+0.6": probs["+0.6"] ?? 0,
  };
}

// ============================================================================
// Constructor Colors
// ============================================================================

/**
 * Returns the hex color for a constructor by slug.
 * Used to render the team color bar in budget builder cards.
 */
export function getConstructorColor(slug: string): string {
  const colorMap: Record<string, string> = {
    "red-bull": "#1E41FF",
    "red-bull-racing": "#1E41FF",
    mclaren: "#FF8000",
    ferrari: "#DC0000",
    mercedes: "#00D2BE",
    "aston-martin": "#006F62",
    alpine: "#0090FF",
    rb: "#2B4562",
    "visa-cash-app-rb": "#2B4562",
    haas: "#FFFFFF",
    williams: "#005AFF",
    sauber: "#52E252",
    "kick-sauber": "#52E252",
  };
  return colorMap[slug] ?? "#888888";
}
